/**
 * Secure Cookie Storage Service
 *
 * Provides a secure alternative to localStorage for storing sensitive data like JWT tokens.
 * Cookies with Secure, SameSite=Strict, and path=/ provide better protection against XSS attacks.
 *
 * Note: For maximum security, the backend should set tokens with HttpOnly flag.
 * Frontend-set cookies cannot be HttpOnly, but still provide better security than localStorage.
 */

interface CookieOptions {
  /** Number of days until the cookie expires */
  maxAge?: number;
  /** Cookie path (default: '/') */
  path?: string;
  /** Secure flag - only transmit over HTTPS (default: true in production) */
  secure?: boolean;
  /** SameSite attribute (default: 'Strict') */
  sameSite?: "Strict" | "Lax" | "None";
}

class CookieStorageService {
  private readonly defaultOptions: CookieOptions = {
    path: "/",
    secure: typeof window !== "undefined" && window.location.protocol === "https:",
    sameSite: "Strict",
    maxAge: 7 // 7 days
  };

  /**
   * Set a cookie with secure defaults
   */
  setItem(key: string, value: string, options: CookieOptions = {}): void {
    try {
      const opts = { ...this.defaultOptions, ...options };

      let cookieString = `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;

      if (opts.maxAge !== undefined) {
        // Convert days to seconds for max-age
        const maxAgeSeconds = opts.maxAge * 24 * 60 * 60;
        cookieString += `; max-age=${maxAgeSeconds}`;
      }

      if (opts.path) {
        cookieString += `; path=${opts.path}`;
      }

      if (opts.secure) {
        cookieString += "; secure";
      }

      if (opts.sameSite) {
        cookieString += `; samesite=${opts.sameSite}`;
      }

      document.cookie = cookieString;
    } catch (error) {
      console.error(`Error saving to cookie storage: ${error}`);
    }
  }

  /**
   * Get a cookie value by key
   */
  getItem(key: string): string | null {
    try {
      const name = encodeURIComponent(key) + "=";
      const cookies = document.cookie.split(";");

      for (let cookie of cookies) {
        cookie = cookie.trim();
        if (cookie.startsWith(name)) {
          return decodeURIComponent(cookie.slice(name.length));
        }
      }

      return null;
    } catch (error) {
      console.error(`Error getting data from cookie storage: ${error}`);
      return null;
    }
  }

  /**
   * Remove a cookie by key
   */
  removeItem(key: string): void {
    try {
      // Set cookie with past expiration date to delete it
      document.cookie = `${encodeURIComponent(key)}=; path=/; max-age=0; secure; samesite=Strict`;
    } catch (error) {
      console.error(`Error removing data from cookie storage: ${error}`);
    }
  }

  /**
   * Clear all cookies accessible to this domain
   * Note: This only clears cookies that were set with the same path
   */
  clear(): void {
    try {
      const cookies = document.cookie.split(";");

      for (const cookie of cookies) {
        const eqPos = cookie.indexOf("=");
        const name = eqPos !== -1 ? cookie.slice(0, Math.max(0, eqPos)).trim() : cookie.trim();
        if (name) {
          this.removeItem(decodeURIComponent(name));
        }
      }
    } catch (error) {
      console.error(`Error clearing cookie storage: ${error}`);
    }
  }

  /**
   * Check if a cookie exists
   */
  hasItem(key: string): boolean {
    return this.getItem(key) !== null;
  }
}

export default new CookieStorageService();
